import type {
  IssueStatement,
  IssueStatementMedia,
  StatementReview,
  StatementRevision,
} from '../../../../api/DigiobcanApi';
import { __, formatDateTime } from '../../../../utils';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  ButtonBase,
  Collapse,
  Stack,
  StatusChip,
  Tooltip,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  /** Vyjadrenia v poradí, v akom sa majú kresliť - od najnovšieho. */
  statements: IssueStatement[];
  /** rozbalená hneď po otvorení nálezu; predvolene zbalená */
  defaultExpanded?: boolean;
  /**
   * Smie prihlásený toto vyjadrenie upraviť (moj-presov#18)? Počíta to
   * nález - pozná archiváciu, kategóriu aj rozpracovanú úpravu.
   */
  canEdit?: (statement: IssueStatement) => boolean;
  onEdit?: (statement: IssueStatement) => void;
  /** Smie prihlásený vyjadrenie odstrániť? Rovnaké pravidlá ako úprava. */
  canDelete?: (statement: IssueStatement) => boolean;
  onDelete?: (statement: IssueStatement) => void;
  /**
   * Vnútorný okraj nesie história sama, nie karta okolo. Na desktope je
   * história karta sekcie ([[IssueSection]]) a jej hlavička sa musí dať
   * kliknúť po celej šírke karty, vrátane okrajov.
   */
  inset?: boolean;
};

/** Výška riadku nadpisu vyjadrenia - tlačidlo „Upraviť" je vyššie než text. */
const TITLE_ROW_HEIGHT = 30;

const personName = (person?: { name?: string; surname?: string } | null) =>
  person
    ? [person.name, person.surname].filter(Boolean).join(' ') || __('Úrad')
    : __('Úrad');

const authorName = (statement: IssueStatement) => personName(statement.author);

const MediaList: React.FC<{
  media: IssueStatementMedia[];
  prefix?: string;
  struck?: boolean;
}> = ({ media, prefix, struck = false }) =>
  media.length ? (
    <Stack gap={0.25}>
      {media.map((item) => (
        <Typography
          key={item.media_id}
          variant='caption'
          component='a'
          href={item.public_url}
          target='_blank'
          rel='noopener noreferrer'
          sx={{ textDecoration: struck ? 'line-through' : undefined }}
        >
          {[prefix, item.original_filename ?? item.media_id]
            .filter(Boolean)
            .join(' ')}
        </Typography>
      ))}
    </Stack>
  ) : null;

/**
 * Kolá schvaľovania - len v administrácii. Pripomienky aj posúdené znenia sú
 * interné, občan vidí len zverejnený text. Posúdené znenie sa vypisuje, len
 * keď sa od výsledného (`text`) líši: pri vrátení je to verzia pred úpravou
 * autora, pri schválení s úpravou autorov text pred zásahom schvaľovateľa.
 * Od najnovšieho, rovnako ako vyjadrenia okolo.
 */
const ReviewRounds: React.FC<{ reviews?: StatementReview[]; text: string }> = ({
  reviews,
  text,
}) =>
  reviews?.length ? (
    <Stack gap={0.75}>
      {[...reviews].reverse().map((review) => (
        <Stack key={review.reviewed_at} gap={0.5}>
          <Typography variant='caption' color='text.secondary'>
            {`${
              review.decision === 'rejected'
                ? __('Vrátil na úpravu')
                : __('Schválil')
            }: ${personName(review.reviewer)} · ${formatDateTime(
              new Date(review.reviewed_at),
            )}`}
          </Typography>
          {review.note && (
            <Typography
              variant='caption'
              sx={{
                px: 1.25,
                py: 0.75,
                borderRadius: 1,
                borderLeft: (theme) => `3px solid ${theme.palette.divider}`,
                backgroundColor: 'grey.50',
              }}
            >
              {review.note}
            </Typography>
          )}
          {review.submitted_text !== text && (
            <Typography variant='caption' color='text.secondary'>
              {`${__('Posúdené znenie')}: ${review.submitted_text}`}
            </Typography>
          )}
        </Stack>
      ))}
    </Stack>
  ) : null;

/**
 * História úprav jedného vyjadrenia (moj-presov#18), od najnovšej - ako
 * úpravy príspevku na sociálnej sieti: kto, kedy, aké znenie a ktoré prílohy
 * pribudli či ubudli. Na konci pôvodné znenie, s ktorým vyjadrenie vzniklo.
 * Rozpracované a stiahnuté úpravy sú tu tiež, so štítkom - občan nevidel ani
 * jednu.
 */
const RevisionLog: React.FC<{
  statement: IssueStatement;
  revisions: StatementRevision[];
}> = ({ statement, revisions }) => (
  <Stack gap={1}>
    <Typography variant='caption' fontWeight={500}>
      {__('História úprav')}
    </Typography>
    {[...revisions].reverse().map((revision) => (
      <Stack key={revision.revision_id} gap={0.5}>
        <Stack direction='row' alignItems='baseline' gap={1} flexWrap='wrap'>
          <Typography variant='caption' color='text.secondary'>
            {`${__('Upravil')}: ${personName(revision.author)} · ${formatDateTime(
              new Date(revision.published_at ?? revision.inserted_at),
            )}`}
          </Typography>
          {revision.approval && (
            <StatusChip tone={APPROVAL_CHIP[revision.approval].tone}>
              {APPROVAL_CHIP[revision.approval].label}
            </StatusChip>
          )}
        </Stack>
        <Typography
          variant='caption'
          color={revision.published_at ? 'text.primary' : 'text.secondary'}
          sx={{ whiteSpace: 'pre-line' }}
        >
          {revision.text}
        </Typography>
        <MediaList media={revision.media_added} prefix='+' />
        <MediaList media={revision.media_removed} prefix='-' struck />
        <ReviewRounds reviews={revision.reviews} text={revision.text} />
      </Stack>
    ))}
    <Stack gap={0.5}>
      <Typography variant='caption' color='text.secondary'>
        {`${__('Pôvodné znenie')} · ${formatDateTime(
          new Date(statement.inserted_at),
        )}`}
      </Typography>
      <Typography
        variant='caption'
        color='text.secondary'
        sx={{ whiteSpace: 'pre-line' }}
      >
        {revisions[0].previous_text}
      </Typography>
    </Stack>
  </Stack>
);

/**
 * Štítok schválenia. Vyjadrenie, ktoré schváleniu nepodliehalo, žiadny nemá -
 * je to obyčajné vyjadrenie ako doteraz a štítok by pri každom z nich hovoril
 * niečo, na čo sa nikto nepýtal.
 */
const APPROVAL_CHIP: Record<
  NonNullable<IssueStatement['approval']>,
  { tone: 'warning' | 'success' | 'neutral'; label: string }
> = {
  pending: { tone: 'warning', label: __('Čaká na schválenie') },
  rejected: { tone: 'warning', label: __('Vrátené na úpravu') },
  approved: { tone: 'success', label: __('Schválené') },
  withdrawn: { tone: 'neutral', label: __('Stiahnuté') },
};

/** Bodka na časovej osi: nezverejnené vyjadrenie nesmie vyzerať ako zverejnené. */
const dotColor = (statement: IssueStatement) => {
  if (statement.approval === 'withdrawn' || statement.deleted_at)
    return 'grey.400';
  if (statement.approval === 'pending' || statement.approval === 'rejected')
    return 'warning.light';
  return statement.final ? 'success.main' : 'warning.main';
};

/**
 * Vyjadrenia k nálezu ako **časová os**, od najnovšieho po najstaršie,
 * **zbalená** pod jeden riadok „História vyjadrení".
 *
 * Kedysi to boli dva rôzne tvary na tej istej veci (finálne stanovisko ako
 * modrý blok nad formulárom, zvyšok ako zoznam pod ním); potom jeden prúd od
 * najstaršieho **nad** formulárom. Ani to neobstálo: s kolami schvaľovania
 * mal nález obrazovky histórie a rozhodovanie až pod nimi. Dnes stojí
 * rozhodovanie hore, história pod ním a otvára sa na požiadanie - najnovšie
 * navrchu, lebo to je to, na čo sa úradník pozerá.
 *
 * Farbu nesie **bodka a nadpis**, nie plocha: v karte nálezu je už podfarbený
 * pruh hlavičky a informačné bloky o presune či duplicite, a ďalšie farebné
 * plochy by z nej urobili mozaiku. Zelená je finálne stanovisko (nález sa ním
 * uzavrel), jantárová priebežné - tá istá dvojica ako v stavoch nálezu.
 */
/**
 * „upravené 23. 10. 2026 9:38" s históriou úprav v tooltipe. História
 * nestojí v karte: pri každom vyjadrení by zaberala viac miesta než ono samo,
 * a úradník ju potrebuje len občas - ako „upravené" pri príspevku na sociálnej
 * sieti. Tooltip je svetlý a interaktívny (MUI predvolene), aby sa dali
 * otvoriť prílohy úprav.
 *
 * Vyjadrenie, ktoré má len nezverejnené úpravy (čakajúcu, stiahnutú), štítok
 * „upravené" nemá - občan ho za upravené nepovažuje - ale históriu áno.
 */
export const StatementEditedLabel: React.FC<{
  statement: IssueStatement;
}> = ({ statement }) => {
  const revisions = statement.revisions ?? [];
  if (!revisions.length) return null;

  return (
    <Tooltip
      placement='bottom-start'
      title={<RevisionLog statement={statement} revisions={revisions} />}
      slotProps={{
        tooltip: {
          sx: {
            p: 1.5,
            maxWidth: 420,
            maxHeight: 480,
            overflowY: 'auto',
            color: 'text.primary',
            backgroundColor: 'background.paper',
            border: (theme) => `1px solid ${theme.palette.divider}`,
            boxShadow: 3,
          },
        },
      }}
    >
      <Box
        component='span'
        sx={{
          cursor: 'help',
          textDecoration: 'underline dotted',
          textUnderlineOffset: 3,
        }}
      >
        {statement.edited_at
          ? `${__('upravené')} ${formatDateTime(new Date(statement.edited_at))}`
          : __('história úprav')}
      </Box>
    </Tooltip>
  );
};

const IssueStatementHistory: React.FC<Props> = ({
  statements,
  defaultExpanded = false,
  canEdit,
  onEdit,
  canDelete,
  onDelete,
  inset = false,
}) => {
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  if (!statements.length) return null;

  return (
    <Stack>
      <ButtonBase
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
        sx={{
          alignSelf: 'stretch',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: 0.75,
          ...(inset ? { px: 2.5, py: 2 } : { py: 0.5, borderRadius: 1 }),
        }}
      >
        <Typography variant='body2' fontWeight={500} lineHeight={1.5}>
          {__('História vyjadrení')}
        </Typography>
        <Typography variant='caption' color='text.secondary' lineHeight={1.5}>
          {`${statements.length} · ${__('posledné')} ${formatDateTime(
            new Date(statements[0].inserted_at),
          )}`}
        </Typography>
        <ExpandMoreIcon
          fontSize='small'
          sx={{
            ml: 'auto',
            color: 'text.secondary',
            transition: 'transform 150ms',
            transform: expanded ? 'rotate(180deg)' : 'none',
          }}
        />
      </ButtonBase>

      <Collapse in={expanded}>
        <Stack sx={inset ? { px: 2.5, pb: 2.5 } : { pt: 1.25 }}>
          {statements.map((statement, index) => {
            const last = index === statements.length - 1;
            // Odstránené vyjadrenie (000039) ostáva v histórii prečiarknuté -
            // občan ho už nevidí, úradník musí vidieť, že bolo a kto ho vzal.
            const deleted = !!statement.deleted_at;

            return (
              <Stack key={statement.statement_id} direction='row' gap={1.25}>
                {/* Stĺpik s bodkou. Čiara nie je pod poslednou položkou - viedla by
                do prázdna, respektíve do formulára, ktorý s históriou nesúvisí. */}
                <Stack alignItems='center' sx={{ flex: 'none', width: 10 }}>
                  {/* Bodka v strede riadku nadpisu, nie na jeho vrchu: riadok
                  má pevnú výšku `TITLE_ROW_HEIGHT` kvôli tlačidlu „Upraviť". */}
                  <Box
                    sx={{
                      flex: 'none',
                      height: TITLE_ROW_HEIGHT,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: dotColor(statement),
                      }}
                    />
                  </Box>
                  {!last && (
                    <Box
                      sx={{
                        flex: 1,
                        width: '1px',
                        my: 0.5,
                        backgroundColor: 'divider',
                      }}
                    />
                  )}
                </Stack>

                <Stack
                  gap={0.5}
                  sx={{ flex: 1, minWidth: 0, pb: last ? 0 : 1.5 }}
                >
                  <Stack
                    direction='row'
                    alignItems='center'
                    gap={1}
                    flexWrap='wrap'
                    sx={{ minHeight: TITLE_ROW_HEIGHT }}
                  >
                    <Typography
                      variant='body2'
                      fontWeight={700}
                      color={
                        deleted
                          ? 'text.secondary'
                          : statement.final &&
                              (!statement.approval ||
                                statement.approval === 'approved')
                            ? 'success.dark'
                            : 'text.primary'
                      }
                    >
                      {statement.final
                        ? __('Finálne vyjadrenie')
                        : __('Priebežné vyjadrenie')}
                    </Typography>
                    {deleted && (
                      <StatusChip tone='neutral'>{__('Odstránené')}</StatusChip>
                    )}
                    {statement.approval && (
                      <StatusChip tone={APPROVAL_CHIP[statement.approval].tone}>
                        {APPROVAL_CHIP[statement.approval].label}
                      </StatusChip>
                    )}
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ flex: 1 }}
                    >
                      {formatDateTime(new Date(statement.inserted_at))}
                      {!!statement.revisions?.length && (
                        <>
                          {' · '}
                          <StatementEditedLabel statement={statement} />
                        </>
                      )}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {authorName(statement)}
                    </Typography>
                    {onEdit && canEdit?.(statement) && (
                      <Button
                        variant='text'
                        size='small'
                        onClick={() => onEdit(statement)}
                      >
                        {__('Upraviť')}
                      </Button>
                    )}
                    {onDelete && canDelete?.(statement) && (
                      <Button
                        variant='text'
                        size='small'
                        onClick={() => onDelete(statement)}
                      >
                        {__('Odstrániť')}
                      </Button>
                    )}
                  </Stack>

                  <Stack
                    gap={0.75}
                    sx={{
                      p: 1.25,
                      borderRadius: 1,
                      backgroundColor: 'grey.50',
                    }}
                  >
                    <Typography
                      variant='body2'
                      color={
                        statement.approval === 'withdrawn' || deleted
                          ? 'text.secondary'
                          : 'text.primary'
                      }
                      sx={{
                        textDecoration: deleted ? 'line-through' : undefined,
                      }}
                    >
                      {statement.text}
                    </Typography>

                    <MediaList media={statement.media} struck={deleted} />
                  </Stack>

                  {deleted && (
                    <Typography variant='caption' color='text.secondary'>
                      {`${__('Odstránil')}: ${personName(
                        statement.deleted_by,
                      )} · ${formatDateTime(new Date(statement.deleted_at!))}`}
                    </Typography>
                  )}

                  {/* Kolá schvaľovania samotného vyjadrenia. Úprava ich má
                  vlastné, v histórii úprav nižšie. Pri upravenom vyjadrení sa
                  posúdené znenie porovnáva s prvotným, nie s dnešným - inak
                  by sa pri každom kole vypísalo, hoci ho nikto nemenil. */}
                  <ReviewRounds
                    reviews={statement.reviews}
                    text={
                      statement.revisions?.find((r) => r.published_at)
                        ?.previous_text ?? statement.text
                    }
                  />
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </Collapse>
    </Stack>
  );
};

export default IssueStatementHistory;
